import React, { useState } from 'react';
import { X, Truck, Loader2, AlertCircle } from 'lucide-react';
import styles from '../Vendors.module.css';
import { vendorFetch, errText } from '../../../utils/vendorApi';

/** Add or edit a vendor */
export default function VendorForm({ vendor, storeId, onClose, onSaved }) {
    const editing = Boolean(vendor?._id);
    const ob = Number(vendor?.openingBalance) || 0;
    const [f, setF] = useState({
        name: vendor?.name || '', contactPerson: vendor?.contactPerson || '', phone: vendor?.phone || '', email: vendor?.email || '',
        gstin: vendor?.gstin || '', address: vendor?.address || '', paymentTermsDays: vendor?.paymentTermsDays ?? 15, notes: vendor?.notes || '',
        opening: Math.abs(ob) || '', openingSide: ob < 0 ? 'THEY_OWE' : 'WE_OWE',
    });
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

    const save = async () => {
        if (!f.name.trim()) { setError('Enter the vendor name.'); return; }
        setSaving(true); setError('');
        try {
            const body = {
                name: f.name, contactPerson: f.contactPerson, phone: f.phone, email: f.email, gstin: f.gstin, address: f.address,
                paymentTermsDays: Number(f.paymentTermsDays) || 0, notes: f.notes,
                openingBalance: (Number(f.opening) || 0) * (f.openingSide === 'THEY_OWE' ? -1 : 1),
            };
            const json = await vendorFetch(editing ? `/${vendor._id}` : '', { method: editing ? 'PUT' : 'POST', body, storeId });
            onSaved?.(json.data, json.message);
        } catch (e) { setError(errText(e)); } finally { setSaving(false); }
    };

    return (
        <div className={styles.overlay} onMouseDown={(e) => e.target === e.currentTarget && !saving && onClose()} role="presentation">
            <div className={styles.modal} role="dialog" aria-modal="true" aria-label={editing ? 'Edit vendor' : 'Add vendor'}>
                <div className={styles.modalHead}>
                    <span className={styles.modalIcon}><Truck size={18} /></span>
                    <div><h3>{editing ? 'Edit vendor' : 'Add vendor'}</h3><p>Supplier for this branch</p></div>
                    <button type="button" className={styles.modalClose} onClick={onClose} aria-label="Close"><X size={17} /></button>
                </div>
                <div className={styles.modalBody}>
                    {error && <div className={`${styles.msg} ${styles.msgErr}`}><AlertCircle size={14} /><span>{error}</span></div>}
                    <label className={styles.field}><span>Vendor / company name <em>*</em></span><input className={styles.input} value={f.name} onChange={set('name')} maxLength={120} autoFocus placeholder="e.g. Glamour Distributors" /></label>
                    <div className={styles.grid2}>
                        <label className={styles.field}><span>Contact person</span><input className={styles.input} value={f.contactPerson} onChange={set('contactPerson')} maxLength={80} /></label>
                        <label className={styles.field}><span>Phone</span><input className={styles.input} value={f.phone} onChange={set('phone')} inputMode="tel" placeholder="98765 43210" /></label>
                        <label className={styles.field}><span>Email</span><input className={styles.input} type="email" value={f.email} onChange={set('email')} /></label>
                        <label className={styles.field}><span>GSTIN</span><input className={styles.input} value={f.gstin} onChange={(e) => setF((x) => ({ ...x, gstin: e.target.value.toUpperCase() }))} maxLength={15} placeholder="Optional" /></label>
                    </div>
                    <div className={styles.grid2}>
                        <label className={styles.field}>
                            <span>Payment terms (days)</span>
                            <input className={styles.input} type="number" min="0" max="365" value={f.paymentTermsDays} onChange={set('paymentTermsDays')} />
                            <small>Bills are due this many days after the bill date. 0 = pay on delivery.</small>
                        </label>
                        <label className={styles.field}>
                            <span>Opening balance</span>
                            <div style={{ display: 'flex', gap: 6 }}>
                                <input className={styles.input} type="number" min="0" value={f.opening} onChange={set('opening')} placeholder="0" style={{ flex: 1 }} />
                                <select className={styles.input} style={{ width: 'auto' }} value={f.openingSide} onChange={set('openingSide')} aria-label="Who owes">
                                    <option value="WE_OWE">We owe</option><option value="THEY_OWE">They owe us</option>
                                </select>
                            </div>
                            <small>Money pending from before you started using Hirsta.</small>
                        </label>
                    </div>
                    <label className={styles.field}><span>Address</span><textarea className={styles.input} rows={2} value={f.address} onChange={set('address')} maxLength={300} /></label>
                    <label className={styles.field}><span>Notes</span><textarea className={styles.input} rows={2} value={f.notes} onChange={set('notes')} maxLength={500} placeholder="Brands supplied, delivery days, bank details..." /></label>
                </div>
                <div className={styles.modalFoot}>
                    <div className={styles.footRight}>
                        <button type="button" className={styles.btn} onClick={onClose} disabled={saving}>Cancel</button>
                        <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving}>{saving && <Loader2 size={14} className={styles.spin} />} {editing ? 'Save' : 'Add vendor'}</button>
                    </div>
                </div>
            </div>
        </div>
    );
}
